import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useCallback, useMemo, useState, type FC } from 'react';
import {
  PiArchiveLight,
  PiArrowCounterClockwiseLight,
  PiDotsThreeVertical,
  PiInfoLight,
} from 'react-icons/pi';
import { ApiError, FunnelInteractionDirection } from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useConversationPropertyPreview } from 'src/modules/communications/hooks/use-conversation-property-preview';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import type { ConversationCardProps } from 'src/modules/communications/types/conversation-card.type';
import { isTestConversationDocument } from 'src/modules/communications/utils/test-conversation.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { Text } from 'src/modules/shared/components/atoms/text';
import { getColorClass } from 'src/modules/shared/components/atoms/text/text.styles';
import Dropdown, {
  type DropdownItem,
} from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { usePeriodicRerender } from 'src/modules/shared/hooks/render';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { formatTimeDistance } from 'src/modules/shared/utils/formatting/date.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { Route as CatalogPropertiesEditRoute } from 'src/routes/catalog/properties.$propertyId.edit';
import { useArchiveFunnelConversation } from '../../../../hooks/api/funnels/funnel-conversations-mutations';
import { ConversationCardFooter } from './card-footer';
import { ConversationCardHeader } from './card-header';
import { ConversationCardInteraction } from './card-interaction';
import { ConversationPropertySummary } from './property-summary';

/**
 * Shows one responsive conversation card with its authorized property summary.
 *
 * This is an independent fork of the previous implementation at
 * `conversations/card/index.tsx` from revision `dfc18ccd453a15680eab7cb34abe3c61cfdb0133`.
 * The prior implementation now lives at `conversations/legacy-card/`.
 *
 * @param props - Conversation content, selection, contextual status, and preview mode.
 * @returns One card tree that responds to its own available width.
 */
export const ConversationCard: FC<ConversationCardProps> = ({
  conversation,
  isSelected,
  onClick,
  className = '',
  showStatus = false,
  onOpenDetails,
  cacheOnly = false,
}) => {
  const { showAlert } = useAlert();
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  usePeriodicRerender(5000);

  const isArchived = !!conversation.archivedAt;
  const conversationId = Number(conversation.id);
  const parsedPropertyId =
    conversation.propertyId == null ? null : Number(conversation.propertyId);
  const propertyId =
    parsedPropertyId !== null &&
    Number.isInteger(parsedPropertyId) &&
    parsedPropertyId > 0
      ? parsedPropertyId
      : null;
  const propertyPreview = useConversationPropertyPreview(propertyId, {
    cacheOnly,
  });
  const handleOpenProperty = useCallback(() => {
    if (
      propertyId === null ||
      propertyPreview.state !== 'ready' ||
      propertyPreview.preview == null
    ) {
      return;
    }

    // TanStack widens route options; its route types retain the configured validator.
    const validateSearch = CatalogPropertiesEditRoute.options
      .validateSearch as typeof CatalogPropertiesEditRoute.types.searchValidator;
    void navigate({
      to: CatalogPropertiesEditRoute.to,
      params: { propertyId },
      search: (previous) => validateSearch(previous),
    });
  }, [navigate, propertyId, propertyPreview.preview, propertyPreview.state]);

  const { archiveConversation: toggleArchive } = useArchiveFunnelConversation(
    !isArchived,
    () => setSubmitting(false),
    (error) => {
      setSubmitting(false);

      if (error instanceof ApiError) {
        const serializedErrorBody = JSON.stringify(error.body);
        if (
          serializedErrorBody.includes(
            'Move the conversation to a nonterminal status before unarchiving'
          )
        ) {
          showAlert(
            t('communications.conversation.moveToOpenBeforeRestore'),
            'error'
          );
        }
      }
    }
  );

  const handleArchiveAction = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await toggleArchive(conversationId);
    } catch {
      // Error handled in hook callbacks
    } finally {
      setSubmitting(false);
    }
  }, [submitting, toggleArchive, conversationId]);

  const contactId = useMemo(() => {
    const contact = conversation.contacts?.[0];
    const contactCandidates = [contact?.id, conversation.contactId];

    for (const candidate of contactCandidates) {
      const parsedCandidate = Number(candidate);

      if (!Number.isNaN(parsedCandidate) && parsedCandidate > 0) {
        return parsedCandidate;
      }
    }

    return null;
  }, [conversation.contactId, conversation.contacts]);

  const menuItems = useMemo((): DropdownItem[] => {
    const items: DropdownItem[] = [];

    if (contactId && onOpenDetails) {
      items.push({
        icon: (
          <PiInfoLight
            className={`h-4 w-4 shrink-0 ${getColorClass('secondary')}`}
          />
        ),
        content: 'Ver detalles',
        onClick: () => onOpenDetails(contactId),
      });
    }

    items.push(
      isArchived
        ? {
            icon: (
              <PiArrowCounterClockwiseLight
                className={`h-4 w-4 shrink-0 ${getColorClass('secondary')}`}
              />
            ),
            content: 'Restaurar',
            onClick: () => void handleArchiveAction(),
            disabled: submitting,
          }
        : {
            icon: (
              <PiArchiveLight
                className={`h-4 w-4 shrink-0 ${getColorClass('danger')}`}
              />
            ),
            content: (
              <Text
                size="paragraph"
                weight="light"
                color="danger"
                className="text-left"
              >
                Archivar
              </Text>
            ),
            onClick: () => void handleArchiveAction(),
            disabled: submitting,
          }
    );

    return items;
  }, [contactId, handleArchiveAction, isArchived, onOpenDetails, submitting]);

  const replyNeeded =
    conversation.lastInteraction?.direction ===
    FunnelInteractionDirection.INCOMING;
  const statusQuery = useMemo(() => {
    if (!showStatus || !conversation.funnelId) return {};
    return createQuery()
      .equals('funnelId', Number(conversation.funnelId))
      .orderByAsc('position')
      .build();
  }, [conversation.funnelId, showStatus]);
  const { statuses } = useFunnelStatuses(
    statusQuery,
    showStatus && !!conversation.funnelId
  );
  const currentStatus = useMemo(() => {
    if (!conversation.funnelStatusId) return undefined;
    return statuses.find(
      (status) => Number(status.id) === Number(conversation.funnelStatusId)
    );
  }, [conversation.funnelStatusId, statuses]);

  const { users } = useAllPersonAndSystemUsers();
  const assignedUserName = useMemo(() => {
    if (!conversation.userId) {
      return 'Sin asignar';
    }

    const assignedUser = users.find(
      (user) => Number(user.id) === Number(conversation.userId)
    );

    if (!assignedUser) {
      return 'Sin asignar';
    }

    return formatFullName(
      assignedUser.firstName,
      assignedUser.lastName,
      assignedUser.email
    );
  }, [conversation.userId, users]);

  const formattedTime = conversation.lastInteractionAt
    ? formatTimeDistance(
        new Date(conversation.lastInteractionAt),
        false,
        language
      )
    : '';
  const createdAt = new Date(conversation.createdAt);
  const createdTimeLabel =
    !conversation.lastInteractionAt && Number.isFinite(createdAt.getTime())
      ? formatTimeDistance(createdAt, false, language)
      : '';
  const isTestConversation = isTestConversationDocument(conversation);

  return (
    <motion.div
      ref={propertyPreview.ref}
      className={`[container-type:inline-size] w-full max-w-full ${className}`}
    >
      <Card
        onClick={onClick}
        size="md"
        isHighlighted={isSelected}
        className={`min-w-0 !rounded-xl !px-[13px] !py-[11px] relative overflow-hidden transition-all duration-200 ${
          isArchived ? 'opacity-80' : ''
        }`}
        variant="neutral"
        appearance="outline"
        shadow="none"
      >
        <div className="min-w-0">
          <ConversationCardHeader
            conversation={conversation}
            formattedTime={formattedTime}
            createdTimeLabel={createdTimeLabel}
            showTestBadge={false}
            className="mb-1.5 h-[35px] min-h-[35px]"
            actionSlot={
              menuItems.length > 0 ? (
                <div
                  role="presentation"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Dropdown
                    items={menuItems}
                    placement="bottom-end"
                    mobileTitle="Acciones de conversación"
                    handler={
                      <Button
                        aria-label={t(
                          'communications.conversationHeader.moreActions'
                        )}
                        variant="neutral"
                        appearance="ghost"
                        size="sm"
                        className="!h-6 !min-h-0 !w-[22px] !p-0.5"
                        isIconOnly
                        disabled={submitting}
                      >
                        <PiDotsThreeVertical className="h-5 w-5 shrink-0" />
                      </Button>
                    }
                  />
                </div>
              ) : null
            }
          />

          <div className="min-w-0">
            <div className="mb-2 mt-[3px] h-[17px] min-w-0">
              <ConversationCardInteraction
                interaction={conversation.lastInteraction}
              />
            </div>
            {propertyId !== null && (
              <div className="min-w-0">
                <ConversationPropertySummary
                  propertyId={propertyId}
                  state={propertyPreview.state}
                  property={propertyPreview.preview}
                  imageUrl={propertyPreview.imageUrl}
                  onImageError={propertyPreview.onImageError}
                  onOpenProperty={cacheOnly ? undefined : handleOpenProperty}
                  t={t}
                />
              </div>
            )}
          </div>

          <ConversationCardFooter
            isTestConversation={isTestConversation}
            status={showStatus ? currentStatus : undefined}
            isArchived={isArchived}
            replyNeeded={replyNeeded}
            assignedUserName={assignedUserName}
          />
        </div>
      </Card>
    </motion.div>
  );
};
